import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Download, Plus, Search } from 'lucide-react'
import { getJournal } from '../../api/accounting'
import Spinner from '../../components/Spinner'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { HelpTip } from '../../components/HelpTip'
import { fmtDay, localToday } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money } from '../sales/salesShared'
import { PERIODS, periodRange } from '../operating/expensesShared'
import AccountingTabs from './AccountingTabs'
import { STATUS_TONE } from './accountingShared'

const SHOW = [
  { id: 'all', label: 'All' },
  { id: 'drafts', label: 'Drafts', status: 'Draft' },
  { id: 'posted', label: 'Posted', status: 'Posted' },
  { id: 'voided', label: 'Voided', status: 'Voided' },
]

// Every journal entry: made here by hand, or by sales, purchases, expenses,
// depreciation and the rest of the app.
export default function JournalEntries() {
  const nav = useNavigate()
  const [params, setParams] = useSearchParams()
  const show = SHOW.some((s) => s.id === params.get('show')) ? params.get('show') : 'all'
  const period = PERIODS.some((p) => p.id === params.get('period')) ? params.get('period') : (show === 'drafts' ? 'all' : 'month')
  const setParam = (key, value, fallback) => setParams((prev) => {
    const next = new URLSearchParams(prev)
    if (value === fallback) next.delete(key); else next.set(key, value)
    return next
  }, { replace: true })
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [source, setSource] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    setLoading(true)
    const [start, end] = periodRange(period)
    getJournal({ start, end })
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [period])

  const sources = useMemo(() => [...new Set(rows.map((r) => r.source.label))].sort(), [rows])
  const bySource = useMemo(() => rows.filter((r) => !source || r.source.label === source), [rows, source])
  const counts = useMemo(() => Object.fromEntries(SHOW.map((s) => [s.id, bySource.filter((r) => !s.status || r.status === s.status).length])), [bySource])
  const shown = useMemo(() => {
    const status = SHOW.find((s) => s.id === show)?.status
    const term = search.trim().toLowerCase()
    return bySource.filter((r) => (!status || r.status === status)
      && (!term || [docNo('je', r.je_no), r.memo, r.source.label, ...r.accounts].join(' ').toLowerCase().includes(term)))
  }, [bySource, show, search])

  const exportCsv = () => {
    const head = ['Date', 'Entry', 'Description', 'Source', 'Status', 'Amount', 'Accounts']
    const lines = shown.map((r) => [r.date, docNo('je', r.je_no), r.memo, r.source.label, r.status, Number(r.amount).toFixed(2), r.accounts.join('; ')])
    const csv = [head, ...lines].map((a) => a.map((x) => `"${String(x ?? '').replaceAll('"', '""')}"`).join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `journal_entries_${localToday()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="ci-page">
      <AccountingTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Journal entries <HelpTip k="journal" /></h1>
            <div className="ci-title-meta">Every change to the books, made here or by the rest of the app.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button type="button" className="ci-btn ci-btn-ghost" onClick={exportCsv} disabled={!shown.length}><Download size={15} />Export</button>
          <Link data-write className="ci-btn ci-btn-primary" to="/accounting/journal/new"><Plus size={15} />New journal entry</Link>
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search journal entries">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Entry number, description or account…" />
        </label>
        <select className="ci-select" value={period} onChange={(e) => setParam('period', e.target.value, show === 'drafts' ? 'all' : 'month')} aria-label="Period">
          {PERIODS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <select className="ci-select" value={source} onChange={(e) => setSource(e.target.value)} aria-label="Source">
          <option value="">All sources</option>
          {sources.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <FilterChips options={SHOW.map((s) => ({ ...s, count: counts[s.id] }))} value={show} onChange={(id) => setParam('show', id, 'all')} />

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead>
              <tr>
                <th>Date</th>
                <th>Entry</th>
                <th className="hide-sm">Source <HelpTip k="source" /></th>
                <th className="center">Status</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5"><Spinner label="Loading journal entries…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="5"><div className="ci-empty">{rows.length ? 'No entries match.' : 'No journal entries in this period.'}</div></td></tr>
              ) : shown.map((r) => (
                <tr key={r.je_id} className="ci-row-link" onClick={(e) => { if (!e.target.closest('a')) nav(`/accounting/journal/${r.je_id}`) }}>
                  <td className="ci-muted-cell">{fmtDay(r.date)}</td>
                  <td>
                    <Link className="ci-strong acc-row-link" to={`/accounting/journal/${r.je_id}`}>
                      {docNo('je', r.je_no)} · {r.memo || 'No description'}
                    </Link>
                    <span className="cell-sub">{r.accounts.slice(0, 3).join(' · ')}{r.accounts.length > 3 ? ' · …' : ''}</span>
                  </td>
                  <td className="hide-sm">
                    {r.source.link ? <Link className="acc-source-link" to={r.source.link}>{r.source.label}</Link> : r.source.label}
                  </td>
                  <td className="center"><StatusBadge label={r.status} tone={STATUS_TONE[r.status]} /></td>
                  <td className="num ci-total">{money(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination">
          <span className="ci-page-info">{shown.length} {shown.length === 1 ? 'entry' : 'entries'}</span>
        </div>
      </div>
    </div>
  )
}
